import { useNavigate, useLocation } from 'react-router-dom';
import { Play, Pause, X } from 'lucide-react';
import { usePodcastKeepalive } from '../contexts/PodcastKeepaliveContext.js';
import { MediaImage } from './ui/MediaImage.js';

/**
 * Persistent mini-player bar shown on all routes except the podcast detail page.
 * The actual YouTube iframe stays alive in the PodcastKeepaliveLayer (visibility:hidden).
 * This bar shows the current subtitle, play/pause, and a "back" tap target.
 */
export function MiniPlayerBar() {
  const { persistedPodcastId, miniData, getPlayerControls, clearPersisted } = usePodcastKeepalive();
  const navigate = useNavigate();
  const location = useLocation();

  // Only show when there's an active podcast AND user is NOT on that podcast's page
  const isPodcastPage =
    location.pathname.startsWith('/podcasts/') && location.pathname !== '/podcasts';
  if (!persistedPodcastId || !miniData || isPodcastPage) return null;

  const handleBack = () => navigate(`/podcasts/${persistedPodcastId}`);
  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    getPlayerControls()?.playPause();
  };
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearPersisted();
  };

  return (
    // bottom-16 on mobile (above BottomNav h-16), bottom-0 on desktop (md:hidden BottomNav)
    <div className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
      <div className="pointer-events-auto">
        {/* Progress accent line */}
        <div className="h-[2px]" style={{ background: 'var(--xd-gradient-top-accent)' }} />

        <div
          className="flex items-center gap-3 px-3 h-14 bg-xd-chrome/95 backdrop-blur-md border-t border-[var(--xds-bd2)] cursor-pointer"
          onClick={handleBack}
        >
          {/* Thumbnail */}
          <MediaImage
            src={miniData.thumbnailUrl}
            alt=""
            className="w-9 h-6 rounded-md object-cover border border-[var(--xds-bd2)] shrink-0"
            fallbackClassName="w-9 h-6 rounded-md border border-[var(--xds-bd2)] shrink-0"
            iconClassName="h-3 w-3"
          />

          {/* Title + current subtitle */}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-[var(--xds-fg)] truncate leading-tight">
              {miniData.title}
            </p>
            {miniData.subtitleText && (
              <p className="text-[10px] text-[var(--xd-fg-3)] truncate leading-tight mt-0.5 italic">
                {miniData.subtitleText}
              </p>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              onClick={handlePlayPause}
              className="p-2 bg-xd-action text-xd-action-fg rounded-full w-8 h-8 flex items-center justify-center hover:scale-105 active:scale-90 transition-transform"
              aria-label={miniData.isPlaying ? 'Pausar' : 'Reproducir'}
            >
              {miniData.isPlaying ? (
                <Pause className="h-3.5 w-3.5" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              onClick={handleClose}
              className="p-2 text-[var(--xd-fg-3)] hover:text-[var(--xds-fg)] transition-colors w-8 h-8 flex items-center justify-center"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
